import React, { useRef, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useSolrContext } from "@e-fork/solr";
import classNames from "classnames";

import NoResults from "../components/system/no-results";
import Loading from "../components/system/loading";
import Error from "../components/system/error";
import EndlessScroll from "../components/system/endless-scroll";
import Filters from "../components/filters";

import actSchemaMapper from "@default/teasers/act/act-schema-mapper";
import { encodeUrl } from "@default/lib/encode-url";
import TeaserScrollOverviewImage from "@default/paragraphs/scroll-overview/components/teaser-scroll-overview-image";
import useScrollActive from "@default/hooks/use-scroll-active";
import ActItem from "./components/act-item";
import useAccessibilityContext from "@default/js/accessibility/store/use-accessibility-context";
import TeaserScrollOverviewContent from "@default/paragraphs/scroll-overview/components/teaser-scroll-overview-content";
import { useSelector } from "react-redux";

const OverviewActs = ({ content }) => {
  const { items, error, config } = useSolrContext();
  const containerRef = useRef(null);
  const [activeId, setActiveId] = useState(null);
  const { simpleDesign } = useAccessibilityContext();
  const isMobile = useSelector((reduxStore) => reduxStore.appStore.isMobile);

  const mappedItems = useMemo(() => items?.map(actSchemaMapper) ?? [], [items]);

  const handleActiveChange = useCallback((id) => setActiveId(id), []);

  useScrollActive(mappedItems, containerRef, handleActiveChange);

  return (
    <section
      className={classNames(
        "paragraph paragraph-overview overview-solr overview-acts",
        `type-${content.fieldContentType}`
      )}
    >
      <div className="container no-padding">
        <div className="row">
          <div className="col-12">
            <Filters />
          </div>
        </div>

        {!!mappedItems.length && !error && (
          <div className="scroll-overview" ref={containerRef}>
            <div className="container content">
              <div className="row list">
                {/* Desktop sticky image pane — hidden on mobile and in simple design */}
                {!simpleDesign && !isMobile ? (
                  <>
                    <div className="col-6 image-wrapper" aria-hidden="true">
                      <div className="absolute-wrapper">
                        <div className="sticky-wrapper">
                          <div className="movement-wrapper">
                            {mappedItems.map((item, index) => (
                              <TeaserScrollOverviewImage
                                key={item.id}
                                item={item}
                                index={index}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Scrollable list pane */}
                    <div
                      className={classNames("teaser-wrapper", {
                        "col-6": !simpleDesign,
                        "col-12": simpleDesign,
                      })}
                    >
                      <ol className="acts-list scroll-wrapper" role="list">
                        {mappedItems.map((item, index) => (
                          <ActItem
                            key={item.id}
                            item={item}
                            index={index}
                            isActive={
                              activeId === encodeUrl(`${item.title}-${item.id}`)
                            }
                          />
                        ))}
                      </ol>
                    </div>
                  </>
                ) : (
                  <div className="acts-list scroll-wrapper" role="list">
                    {mappedItems.map((item, index) => (
                      <TeaserScrollOverviewContent item={item} index={index} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="row">
          <div className="col-12">
            <NoResults />
            <Loading />
            <Error />
          </div>
        </div>

        {config.pageMode === "endless" && <EndlessScroll />}
      </div>
    </section>
  );
};

OverviewActs.propTypes = {
  content: PropTypes.shape({
    fieldContentType: PropTypes.string,
  }),
};

export default OverviewActs;
